import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Truck, ArrowLeftRight, Lock, Star, Check, X, ArrowRight, Footprints, ShieldCheck, Droplet, Cloud } from "lucide-react";
import StoreHeader from "@/components/store/StoreHeader";
import PdpGallery from "@/components/store/PdpGallery";
import ColourSelector from "@/components/product/ColourSelector";
import SizeSelector from "@/components/product/SizeSelector";
import LazyVideo from "@/components/product/LazyVideo";
import ColourDropBanner from "@/components/store/ColourDropBanner";
import PdpDetails from "@/components/store/PdpDetails";
import EmailCapture from "@/components/store/EmailCapture";
import PdpFaq from "@/components/store/PdpFaq";
import StickyBuyBar from "@/components/store/StickyBuyBar";
import CartDrawer from "@/components/product/CartDrawer";
import SizeGuideModal from "@/components/SizeGuideModal";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Reveal, SmallCaps } from "@/components/bits";
import { PRODUCT, PRODUCT_FAQS } from "@/config/product";
import { POGO_COLOURS, SIZE_GROUPS, PREORDER_COLOUR_IDS } from "@/config/pogoConfig";
import { CART_STORAGE_KEY, readStoredCart } from "@/lib/cartStorage";
import { trackPageView, trackBoth } from "@/lib/meta";
import { clarityEvent } from "@/lib/clarity";
import { getDefaultContentId, getVariantContentId } from "@/lib/shopify";

const TRUST = [
  [Truck, "Free shipping AU‑wide"],
  [ArrowLeftRight, "Easy size exchanges"],
  [Lock, "Secure checkout"],
];

// PDP swatch order: Black sits right next to Pink (both preorderable now);
// the coming-soon colourways follow. The waitlist funnel keeps POGO_COLOURS.
const PDP_COLOUR_ORDER = ["pink", "black", "yellow", "light-blue", "lilac"];
const PDP_COLOURS = PDP_COLOUR_ORDER.map((id) =>
  POGO_COLOURS.find((c) => c.id === id)
).filter(Boolean);

// FAQ structured data — mirrors the visible PDP FAQ (same six questions,
// same wording) so AI engines can cite the answers directly.
const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [0, 1, 4, 7, 8, 11].map((i) => ({
    "@type": "Question",
    name: PRODUCT_FAQS[i].q,
    acceptedAnswer: { "@type": "Answer", text: PRODUCT_FAQS[i].a },
  })),
};

// Clarity section-view events: fire only when the section actually scrolls
// into view, once per visit — never twice, even under StrictMode's
// double effect cycle (the fired flag survives the remount).
function useClarityViewOnce(ref, eventName) {
  const fired = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (fired.current) return;
        if (entries.some((e) => e.isIntersecting)) {
          fired.current = true;
          clarityEvent(eventName);
          io.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// The POSSO product page — high-conversion upper experience only:
// PRODUCT → PRICE → COLOUR → SIZE → ADD TO CART → REASSURANCE → AOV.
// Gallery sits directly under the header; title/info stack below it.
// Checkout runs through Shopify (lib/shopify.js + CartDrawer).
export default function ProductPage() {
  const [searchParams] = useSearchParams();
  const [colour, setColour] = useState(
    () => POGO_COLOURS.find((c) => c.id === "pink") || null
  );
  const [size, setSize] = useState(null);
  const [error, setError] = useState("");
  const [cartItems, setCartItems] = useState(readStoredCart);
  const [cartOpen, setCartOpen] = useState(false);
  const [sizePrompt, setSizePrompt] = useState(false);
  const [colourPrompt, setColourPrompt] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);

  // Clarity funnel: one-time view events for the key buy-stack sections.
  const benefitsRef = useRef(null);
  const pricingRef = useRef(null);
  const perksRef = useRef(null);
  useClarityViewOnce(benefitsRef, "BenefitsViewed");
  useClarityViewOnce(pricingRef, "PricingViewed");
  useClarityViewOnce(perksRef, "PreorderPerksViewed");

  // Bag memory: persist across refreshes/navigation (compact colourId form).
  useEffect(() => {
    localStorage.setItem(
      CART_STORAGE_KEY,
      JSON.stringify(
        cartItems.map((l) => ({ colourId: l.colour.id, size: l.size, qty: l.qty }))
      )
    );
  }, [cartItems]);

  useEffect(() => {
    trackPageView("/product");
    // Meta catalogue matching: ViewContent carries the numeric Shopify
    // variant ID of the default in-stock variant (fires once per view).
    let live = true;
    getDefaultContentId()
      .then((cid) => {
        if (!live) return;
        if (cid) {
          trackBoth("ViewContent", {
            content_name: PRODUCT.title,
            content_ids: [cid],
            content_type: "product",
          });
        } else {
          console.error(
            "[meta] ViewContent: Shopify variant ID unresolved — content_ids omitted"
          );
          trackBoth("ViewContent", {
            content_name: PRODUCT.title,
            content_type: "product",
          });
        }
      })
      .catch((e) => {
        console.error("[meta] ViewContent variant lookup failed:", e);
        trackBoth("ViewContent", {
          content_name: PRODUCT.title,
          content_type: "product",
        });
      });
    const pre = searchParams.get("colour");
    if (pre) {
      const match = POGO_COLOURS.find((c) => c.id === pre);
      if (match) setColour(match);
    }
    // Direct-to-cart entry (e.g. the waitlist-surprise reward CTA):
    // preselect the colour and open the bag in size-prompt mode — the pink
    // pair shows as a pending item and no Shopify variant is added until
    // the shopper picks a size.
    const buy = searchParams.get("buy");
    if (buy) {
      const match = POGO_COLOURS.find((c) => c.id === buy);
      if (match) {
        setColour(match);
        setSizePrompt(true);
        setCartOpen(true);
        // Claim analytics: a reward winner landed in the cart — compare
        // against InitiateCheckout for claim-to-checkout conversion.
        trackBoth("RewardClaimed", {
          origin: "waitlist_surprise",
          content_name: PRODUCT.title,
          colour: match.name,
          value: PRODUCT.price,
          currency: PRODUCT.currency,
        });
      }
    }
    window.scrollTo(0, 0);
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickColour = useCallback((c) => {
    setColour(c);
    setError("");
    setColourPrompt(false);
    clarityEvent("ColourSelected");
  }, []);

  const pickSize = useCallback((s) => {
    setSize(s);
    setError("");
    clarityEvent("SizeSelected");
  }, []);

  const addToCart = (items) => {
    setCartItems(items);
    setCartOpen(true);
    // Numeric Shopify variant IDs for Meta catalogue matching — the UI
    // opens instantly; the event fires once IDs resolve from the cached
    // variant map. Unresolved lines are dropped, never guessed.
    Promise.all(
      items.map((i) =>
        getVariantContentId(i.colour.name, i.size).catch(() => null)
      )
    ).then((ids) => {
      const resolved = ids.filter(Boolean);
      if (resolved.length !== items.length) {
        console.error(
          "[meta] AddToCart: variant ID unresolved for",
          items.filter((_, x) => !ids[x]).map((i) => `${i.colour.name} ${i.size}`)
        );
      }
      trackBoth("AddToCart", {
        content_name: PRODUCT.title,
        content_ids: resolved.length ? resolved : undefined,
        content_type: "product",
        value: PRODUCT.price
          ? PRODUCT.price * items.reduce((s, i) => s + i.qty, 0)
          : undefined,
        currency: PRODUCT.currency,
        items: items.map((i) => `${i.colour.name} ${i.size} x${i.qty}`).join(", "),
      });
    });
  };

  // Pink and Black are in this drop — every other colourway routes to the
  // "hasn't dropped yet" notify banner instead of the bag.
  const dropped = !colour || PREORDER_COLOUR_IDS.includes(colour.id);

  const onPreorder = () => {
    if (colour && !dropped) {  setError("");
      document
        .getElementById("colour-drop-banner")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(
        () => document.getElementById("drop-notify-email")?.focus(),
        450
      );
      return;
    }
    if (!size) {
      setError("Select your size first.");
      return;
    }
    if (!colour) {
      setError("Pick your colour first.");
      // Bring the colour circles into view (button still visible below) and
      // give them a gentle pulse so it's clear what to tap.
      setColourPrompt(true);
      const el = document.getElementById("buy-colour-section");
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 76;
        window.scrollTo({ top, behavior: "smooth" });
      }
      setTimeout(() => setColourPrompt(false), 2900);
      return;
    }
    setError("");
    addToCart([{ colour, size, qty: 1 }]);
  };

  // Main CTA wrapper — the sticky bar also calls onPreorder, so the Clarity
  // click event fires here only, never double-counted.
  const onMainPreorder = () => {
    clarityEvent("PreorderButtonClicked");
    onPreorder();
  };

  const openSizeGuide = useCallback(() => {
    setSizeGuideOpen(true);
    clarityEvent("SizeGuideOpened");
  }, []);

  // Sticky-bar preorder with a colour but no size → open the bag slide-out
  // with the size picker inside it.
  const openSizePrompt = useCallback(() => {
    setSizePrompt(true);
    setCartOpen(true);
  }, []);

  const onPromptSizePick = useCallback(
    (s) => {
      if (!colour) return;
      setSize(s);
      setSizePrompt(false);
      addToCart([{ colour, size: s, qty: 1 }]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colour]
  );

  const updateQty = useCallback((idx, delta) => {
    // Minus at qty 1 removes the line from the bag entirely.
    setCartItems((prev) =>
      prev.flatMap((it, i) => {
        if (i !== idx) return [it];
        const next = it.qty + delta;
        return next < 1 ? [] : [{ ...it, qty: next }];
      })
    );
  }, []);

  const addLine = useCallback((line) => {
    setCartItems((prev) => [...prev, line]);
    getVariantContentId(line.colour.name, line.size)
      .catch(() => null)
      .then((cid) => {
        if (!cid) {
          console.error(
            "[meta] AddToCart: variant ID unresolved for",
            `${line.colour.name} ${line.size}`
          );
        }
        trackBoth("AddToCart", {
          content_name: PRODUCT.title,
          content_ids: cid ? [cid] : undefined,
          content_type: "product",
          value: PRODUCT.price,
          currency: PRODUCT.currency,
          colour: line.colour.name,
          size: line.size,
        });
      });
  }, []);

  const price = PRODUCT.price ? `$${PRODUCT.price}` : "$XX";

  const infoStack = (
    <>
      {/* colour — thumbnail row only, no text; sits tight against the
          gallery on mobile, above the title. */}
      <div
        id="buy-colour-section"
        className={`mb-3.5 rounded-2xl transition-shadow ${
          colourPrompt ? "colour-prompt" : ""
        }`}
      >
        <ColourSelector
          values={PDP_COLOURS}
          value={colour}
          onChange={pickColour}
          testIdPrefix="buy-colour"
          hideLabel
          thumbs
          statusFor={(c) =>
            PREORDER_COLOUR_IDS.includes(c.id) ? "Preorder" : "Coming soon"
          }
        />
      </div>

      {/* title + badge (unchanged) + waitlist social proof (unchanged) */}
      <div className="flex items-center justify-between gap-3">
        <h1
          data-testid="pdp-title"
          className="flex-1 min-w-0 font-display uppercase leading-none text-[8vw] sm:text-4xl tracking-wide"
        >
          POSSO<sup className="text-[0.35em] align-super tracking-normal">TM</sup> Work Clog
        </h1>
        <span
          data-testid="buy-preorder-badge"
          className="shrink-0 inline-block font-display uppercase text-[10px] tracking-wide bg-[#FFD84D] border-2 border-ink rounded-full px-3 py-1"
        >
          Pre-order
        </span>
      </div>
      <div
        data-testid="pdp-credibility"
        className="mt-2.5 flex w-full items-center gap-1.5"
      >
        <span className="flex items-center gap-0.5" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star
              key={i}
              className="w-3.5 h-3.5 text-[#FFB800] fill-[#FFB800]"
              strokeWidth={2}
            />
          ))}
        </span>
        <span className="text-xs sm:text-sm font-semibold text-ink/70">
          1,000+ healthcare workers joined the waitlist
        </span>
      </div>

      {/* 1 — four benefits, compact 2x2 with circular icons */}
      <ul
        ref={benefitsRef}
        data-testid="pdp-benefits-row"
        className="mt-4 grid grid-cols-2 gap-x-3 gap-y-4"
      >
        {[
          ["Slip-Resistant Sole", "Grips wet and messy floors.", Footprints, "#FFE4F0", "#FF6BAA"],
          ["Light Toe Protection", "Hardened PVC toe cap.", ShieldCheck, "#FFF3C2", "#D9A400"],
          ["Wipe-Clean Upper", "Easy to clean after messy shifts.", Droplet, "#DDF0FF", "#4EA8FF"],
          ["Gel Arch Insole", "Support underfoot.", Cloud, "#EDE3FF", "#A875FF"],
        ].map(([title, body, Icon, chip, iconColor]) => (
          <li
            key={title}
            data-testid={`pdp-benefit-cell-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="flex items-start gap-2.5"
          >
            <span
              aria-hidden="true"
              className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center"
              style={{ backgroundColor: chip }}
            >
              <Icon className="w-4.5 h-4.5" style={{ color: iconColor }} strokeWidth={2.25} />
            </span>
            <span className="min-w-0">
              <span className="block font-body font-bold uppercase text-[11px] sm:text-xs tracking-wide leading-tight text-ink">
                {title}
              </span>
              <span className="mt-0.5 block text-xs text-ink/60 leading-snug">
                {body}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {/* 2 — price comparison panel: $119 hot pink on the left vs $130
          struck grey on the right, both at the same large size, with the
          SAVE pill + free shipping line beneath the preorder price. */}
      <div
        ref={pricingRef}
        data-testid="price-comparison"
        className="mt-5 rounded-2xl bg-[#FFF0F6] border border-ink/10 px-4 py-3.5"
      >
        <div className="grid grid-cols-[auto_auto_1px_1fr] items-start gap-x-2.5">
          <div>
            <span className="block text-[10px] font-extrabold tracking-[0.14em] uppercase text-[#FF3D8F]">
              Preorder price
            </span>
            <span
              data-testid="buy-price"
              className="mt-0.5 block font-display text-4xl sm:text-5xl leading-none text-[#FF3D8F]"
            >
              {price}
            </span>
          </div>
          <span className="relative justify-self-start mt-1 ml-1.5">
            {/* radiating dashes around the SAVE pill */}
            {[
              "-left-2 top-0 -rotate-45",
              "-left-3 top-1/2 -translate-y-1/2",
              "-left-2 bottom-0 rotate-45",
              "-right-2 top-0 rotate-45",
              "-right-3 top-1/2 -translate-y-1/2",
              "-right-2 bottom-0 -rotate-45",
            ].map((pos) => (
              <span
                key={pos}
                aria-hidden="true"
                className={`absolute w-2 h-[2.5px] rounded-full bg-[#FF6BAA] ${pos}`}
              />
            ))}
            <span
              data-testid="save-badge"
              className="inline-block rounded-full bg-[#FF3D8F] px-3 py-1 font-display uppercase text-[11px] sm:text-xs text-white"
            >
              Save ${PRODUCT.launchPrice - PRODUCT.price}
            </span>
          </span>
          <span aria-hidden="true" className="w-px self-stretch bg-ink/15" />
          <div className="justify-self-end text-right">
            <span className="block text-[9px] font-bold tracking-[0.14em] uppercase text-ink/40">
              Regular price
            </span>
            <span className="mt-0.5 block font-display text-4xl sm:text-5xl leading-none text-ink/35 line-through decoration-[3px]">
              ${PRODUCT.launchPrice}
            </span>
          </div>
          <span
            data-testid="free-shipping-note"
            className="col-start-1 col-end-3 mt-1.5 flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wide text-ink"
          >
            <Truck className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
            + Free Australia-wide shipping
          </span>
        </div>
      </div>

      {/* 3 — preorder vs regular, side by side in one rounded card.
          Left is pale pink with bold text and pink check circles; right
          stays neutral grey with muted ✕ icons. */}
      <div
        ref={perksRef}
        data-testid="perks-comparison"
        className="mt-3 grid grid-cols-2 rounded-2xl border-2 border-ink/10 overflow-hidden"
      >
        <div data-testid="perks-preorder" className="bg-[#FFE4F0] px-3.5 py-3.5">
          <p className="font-body font-bold text-[10px] tracking-[0.14em] uppercase text-[#FF3D8F]">
            Preorder perks
          </p>
          <ul className="mt-2.5 space-y-2.5">
            {[
              `Pay $${PRODUCT.price} (Save $${PRODUCT.launchPrice - PRODUCT.price})`,
              "FREE Australia-wide shipping",
              "Guaranteed to secure your pair",
            ].map((row, i) => (
              <li
                key={row}
                data-testid={`perks-preorder-row-${i + 1}`}
                className="flex items-start gap-2 text-[11px] sm:text-xs font-bold text-ink leading-snug"
              >
                <span className="mt-px w-4 h-4 shrink-0 rounded-full bg-[#FF6BAA] flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                </span>
                {row}
              </li>
            ))}
          </ul>
        </div>
        <div
          data-testid="perks-regular"
          className="bg-[#F4F3F0] border-l-2 border-ink/10 px-3.5 py-3.5"
        >
          <p className="font-body font-bold text-[10px] tracking-[0.14em] uppercase text-ink/40">
            Regular price
          </p>
          <ul className="mt-2.5 space-y-2.5">
            {[
              "No preorder savings",
              "Standard shipping rates",
              "No guarantee your size will be left",
            ].map((row, i) => (
              <li
                key={row}
                data-testid={`perks-regular-row-${i + 1}`}
                className="flex items-start gap-2 text-[11px] sm:text-xs font-semibold text-ink/40 leading-snug"
              >
                <span className="mt-px w-4 h-4 shrink-0 rounded-full bg-ink/15 flex items-center justify-center">
                  <X className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                </span>
                {row}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* size dropdown */}
      <div className="mt-5">
        <SizeSelector
          groups={SIZE_GROUPS}
          value={size}
          onChange={pickSize}
          testIdPrefix="buy-size"
          onSizeGuide={openSizeGuide}
          label="Select your size (EU)"
          thin
        />
      </div>

      {/* 8 — primary CTA */}
      <button
        data-testid="buy-preorder-button"
        onClick={onMainPreorder}
        className="mt-4 w-full min-h-[58px] rounded-full bg-[#FF6BAA] text-ink font-display text-xl uppercase tracking-wide inline-flex items-center justify-center gap-2 border-2 border-ink shadow-[3px_3px_0px_#111111] transition-[transform,box-shadow] duration-150 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#111111] active:translate-x-0 active:translate-y-0 active:shadow-[1px_1px_0px_#111111]"
      >
        {dropped ? (
          <>
            Preorder now — <span data-testid="buy-cta-price">{price}</span>
            <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
          </>
        ) : (
          "Notify me when it drops"
        )}
      </button>

      {dropped && (
        <p
          data-testid="founding-price-note"
          className="mt-2.5 text-center text-sm sm:text-base text-ink font-semibold"
        >
          Save ${PRODUCT.launchPrice - PRODUCT.price} when you preorder + free
          shipping AU-wide.{" "}
          <span className="font-normal text-ink/55">
            Regular price ${PRODUCT.launchPrice} after launch.
          </span>
        </p>
      )}
      {error && (
        <p data-testid="form-error" role="alert" className="mt-2.5 text-sm font-medium text-[#C2271B]">
          {error}
        </p>
      )}
      <div data-testid="trust-row" className="mt-5 flex items-center justify-between gap-2">
        {TRUST.map(([Icon, text]) => (
          <span key={text} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-ink/60">
            <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />
            {text}
          </span>
        ))}
      </div>

      <PdpDetails />

      {colour && !dropped && (
        <ColourDropBanner
          key={colour.id}
          colour={colour}
          onShopPink={() => {
            pickColour(POGO_COLOURS.find((c) => c.id === "pink"));
            openSizePrompt();
          }}
        />
      )}
    </>
  );

  return (
    <div data-testid="product-page" className="bg-ivory text-ink font-body">
      <Seo
        title="POSSO Work Clog — Slip-Resistant, in Pink & Black"
        description="Preorder the POSSO Work Clog for $119 AUD (regular $130) with free Australia-wide shipping. Slip-resistant, closed-toe, EU sizes 35–42."
        path="/product"
        jsonLd={FAQ_LD}
      />
      <StoreHeader cartCount={cartItems.length} onOpenCart={() => setCartOpen(true)} />
      <main>
        <section
          id="buy"
          data-testid="buy-module"
          className="px-4 sm:px-8 pt-0 pb-6 sm:pt-4 max-w-6xl mx-auto w-full"
        >
          {/* Mobile: gallery directly under the header, info below.
              Desktop: two columns (gallery left, info right). */}
          <div className="sm:grid sm:grid-cols-2 sm:gap-10 sm:items-start">
            <Reveal>
              <PdpGallery colour={colour} />
            </Reveal>
            <Reveal className="mt-1 sm:mt-0">{infoStack}</Reveal>
          </div>
        </section>

        {/* Product demo reel — three short silent loops, side by side.
            LazyVideo: posters only until near the viewport, then a single
            best-format source loads and plays while visible. */}
        <section
          data-testid="demo-videos"
          className="pt-10 pb-6 sm:pt-14 sm:pb-10 px-4 sm:px-8 max-w-6xl mx-auto w-full"
        >
          <Reveal>
            <h2 className="font-display uppercase leading-[0.95] text-[8vw] sm:text-4xl text-center mb-5 sm:mb-7">
              Built for chaos
            </h2>
          </Reveal>
          <div className="grid grid-cols-3 gap-2.5 sm:gap-4">
            {[
              ["demo-1", "Leak Proof"],
              ["demo-2", "Reinforced Toe Protection"],
              ["demo-3", "Slip Resistance"],
            ].map(([v, cap], i) => (
              <div key={v}>
                <LazyVideo name={v} testId={`demo-video-${i + 1}`} />
                <p
                  data-testid={`demo-video-caption-${i + 1}`}
                  className="mt-2 text-center font-body font-bold text-[10px] sm:text-xs tracking-[0.14em] uppercase text-ink/60"
                >
                  {cap}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Insole feature — the arch-support insole that ships in every
            pair. Standard PDP section-heading treatment; image left, copy
            right on desktop. */}
        <section
          data-testid="insole-feature"
          className="py-12 sm:py-16 px-4 sm:px-8 max-w-6xl mx-auto w-full"
        >
          <Reveal>
            <SmallCaps>INSIDE EVERY PAIR</SmallCaps>
            <h2 className="mt-3 font-display uppercase leading-[0.95] text-[8vw] sm:text-4xl">
              The insole that carries the shift.
            </h2>
          </Reveal>
          <div className="mt-7 sm:mt-10 sm:grid sm:grid-cols-2 sm:gap-10 sm:items-center">
            <Reveal>
              <img
                data-testid="insole-image"
                src="/images/insole.webp?v=2"
                alt="POSSO arch-support insole — honeycomb-textured top with pink sculpted arch pad and heel cup"
                className="w-full h-auto block rounded-2xl sm:rounded-3xl select-none"
                loading="lazy"
                draggable="false"
              />
            </Reveal>
            <Reveal className="mt-6 sm:mt-0" delay={0.05}>
              <p className="text-base sm:text-lg text-ink/70 max-w-md">
                Every pair ships with our arch-support insole — a sculpted
                arch pad and a deep heel cup, topped with the signature
                honeycomb texture. It pops
                out to rinse, and pops straight back in.
              </p>
              <ul className="mt-5 space-y-3">
                {[
                  ["Signature honeycomb top", "The POSSO pattern — pure design, all ours."],
                  ["Sculpted arch pad", "Support right where a long shift needs it."],
                  ["Deep heel cup", "A held, locked-in feel from clock-in to clock-out."],
                ].map(([t, d], i) => (
                  <li
                    key={t}
                    data-testid={`insole-point-${i + 1}`}
                    className="flex items-start gap-3"
                  >
                    <span className="mt-1.5 w-2.5 h-2.5 rounded-full bg-[#FF6BAA] shrink-0" />
                    <span>
                      <span className="block font-body font-bold text-sm uppercase tracking-wide">
                        {t}
                      </span>
                      <span className="block text-sm text-ink/60">{d}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        <section
          data-testid="brand-message"
          className="px-4 sm:px-8 max-w-6xl mx-auto w-full"
        >
          <div className="mt-10 sm:mt-12 pb-10 sm:pb-12 max-w-2xl mx-auto text-center">
            <Reveal>
              <h2 className="font-display uppercase leading-[0.95] text-[8vw] sm:text-4xl">
                Our story
              </h2>
              <p className="mt-5 text-sm sm:text-base text-ink/70 font-medium leading-relaxed">
                POSSO started with a simple thought: why are the shoes we
                practically live in at work so boring? We wanted something made
                for long shifts that was practical, colourful and actually fun
                to wear.
              </p>
            </Reveal>
          </div>
        </section>

        <PdpFaq onOpenSizeGuide={openSizeGuide} />

        <EmailCapture />
      </main>
      <Footer />
      <CartDrawer
        open={cartOpen}
        items={cartItems}
        onClose={() => {
          setCartOpen(false);
          setSizePrompt(false);
        }}
        sizePrompt={sizePrompt}
        promptColour={colour}
        onSizePick={onPromptSizePick}
        onUpdateQty={updateQty}
        onAddItem={addLine}
        onOpenSizeGuide={openSizeGuide}
      />
      <SizeGuideModal open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} />
      <StickyBuyBar
        colour={colour}
        size={size}
        onPreorder={onPreorder}
        onNeedSize={openSizePrompt}
      />
    </div>
  );
}
